import { __decorate } from "tslib";
import React from 'react';
import { observer } from 'mobx-react';
import { action, observable, computed, isArrayLike } from 'mobx';
import classNames from 'classnames';
import { ProgressType } from 'choerodon-ui/lib/progress/enum';
import KeyCode from 'choerodon-ui/lib/_util/KeyCode';
import debounce from 'lodash/debounce';
import isNil from 'lodash/isNil';
import TextArea from '../text-area/TextArea';
import { TextField } from '../text-field/TextField';
import { ResizeType } from '../text-area/enum';
import Icon from '../icon';
import IntlList from './IntlList';
import { IntlType } from './enum';
import Modal from '../modal/Modal';
import localeContext, { $l } from '../locale-context';
import Progress from '../progress';
import { Size, Tooltip as TextTooltip } from '../core/enum';
import message from '../message';
import exception from '../_util/exception';
import autobind from '../_util/autobind';
import { stopEvent } from '../_util/EventManager';
import isSame from '../_util/isSame';
import isEmpty from '../_util/isEmpty';
import { show } from '../tooltip/singleton';
import isOverflow from '../overflow-tip/util';
let IntlField = class IntlField extends TextArea {
    constructor(props, context) {
        super(props, context);
        this.openModal = async () => {
            if (!this.modal) {
                const { modalProps, maxLengths, type, rows, cols, resize } = this.props;
                const { record, lang, name, element } = this;
                const maxLength = this.getProp('maxLength');
                const { supports } = localeContext;
                const maxLengthList = {};
                Object.keys(supports).map(key => {
                    maxLengthList[key] = maxLengths && (key !== lang || isNil(maxLength))
                        ? maxLengths[key] || maxLength
                        : maxLength;
                    return null;
                });
                if (record && name) {
                    this.setLoading(true);
                    try {
                        if (element && !isSame(this.getValue(), element.value)) {
                            this.syncValueOnBlur(element.value);
                        }
                        await record.tls(name);
                    }
                    catch (err) {
                        message.error(exception(err));
                        return;
                    }
                    finally {
                        this.setLoading(false);
                    }
                    this.storeLocales(record, name);
                }
                this.modal = Modal.open({
                    title: $l('IntlField', 'modal_title'),
                    children: (React.createElement(IntlList, { readOnly: this.readOnly, disabled: this.disabled, record: record, name: name, lang: lang, maxLengths: maxLengthList, type: type, rows: rows, cols: cols, resize: resize, getConfig: this.getContextConfig })),
                    onClose: this.handleIntlListClose,
                    onOk: this.handleIntlListOk,
                    onCancel: this.handleIntlListCancel,
                    okButton: !this.disabled && !this.readOnly,
                    destroyOnClose: true,
                    ...modalProps,
                });
            }
        };
        this.handleIntlListClose = async () => {
            delete this.modal;
            if (!this.props.displayOutput) {
                this.focus();
            }
        };
        const suffixCls = this.props.displayOutput
            ? 'output'
            : (this.props.type !== IntlType.multipleLine ? 'input' : 'textarea');
        this.prefixCls = this.getContextProPrefixCls(suffixCls, props.prefixCls);
    }
    get range() {
        return false;
    }
    get readOnly() {
        if (this.props.displayOutput) {
            return true;
        }
        return this.isReadOnly();
    }
    get resize() {
        if (this.props.displayOutput) {
            return ResizeType.none;
        }
        return super.resize;
    }
    get autoSize() {
        if (this.props.displayOutput) {
            return false;
        }
        return super.autoSize;
    }
    get border() {
        if (this.props.displayOutput) {
            return false;
        }
        return super.border;
    }
    saveSuffixRef(node) {
        this.suffixRef = node;
    }
    getEditorTextInfo(rangeTarget) {
        const superText = super.getEditorTextInfo(rangeTarget);
        return this.props.displayOutput ? { ...superText, placeholder: undefined } : superText;
    }
    getPlaceholders() {
        if (this.props.displayOutput) {
            return [];
        }
        return super.getPlaceholders();
    }
    isEditable() {
        if (this.props.displayOutput) {
            return false;
        }
        return super.isEditable();
    }
    setLoading(loading) {
        this.loading = loading;
    }
    async handleIntlListOk() {
        const { supports } = localeContext;
        const languages = Object.keys(supports);
        const { record, name, field } = this;
        if (record && field) {
            const tlsKey = this.getContextConfig('tlsKey');
            return (await Promise.all(languages.map(language => {
                const intlField = record.dataSet.getField(`${tlsKey}.${name}.${language}`);
                return intlField ? intlField.checkValidity(record) : true;
            }))).every(Boolean);
        }
    }
    async handleIntlListCancel() {
        if (this.disabled || this.readOnly) {
            return;
        }
        const { record, locales } = this;
        if (record && locales) {
            record.set(locales);
        }
    }
    handleKeyDown(e) {
        if (e.keyCode === KeyCode.DOWN && this.props.type !== IntlType.multipleLine) {
            stopEvent(e);
            this.openModal();
        }
        super.handleKeyDown(e);
    }
    handleBlur(e) {
        if (this.modal) {
            e.preventDefault();
        }
        super.handleBlur(e);
    }
    storeLocales(record, name) {
        const tlsKey = `${this.getContextConfig('tlsKey')}.${name}`;
        const tls = record.get(tlsKey);
        const locales = { [name]: record.get(name) };
        if (tls) {
            const { locale: { lang: defaultLang }, supports } = localeContext;
            Object.keys(supports).forEach(lang => {
                const value = tls[lang];
                if (lang === defaultLang) {
                    locales[`${tlsKey}.${lang}`] = locales[name];
                }
                else {
                    locales[`${tlsKey}.${lang}`] = value;
                }
            });
        }
        this.locales = locales;
    }
    getOmitPropsKeys() {
        if (this.props.type === IntlType.multipleLine && !this.props.displayOutput) {
            return super.getOmitPropsKeys().concat([
                'type',
                'displayOutput',
            ]);
        }
        return super.getOmitPropsKeys().concat([
            'cols',
            'rows',
            'wrap',
            'resize',
            'autoSize',
            'onResize',
            'type',
            'displayOutput',
            'modalProps',
        ]);
    }
    getOtherProps() {
        if (this.props.type === IntlType.multipleLine) {
            return super.getOtherProps();
        }
        return TextField.prototype.getOtherProps.call(this);
    }
    getWrapperClassNames(...args) {
        return super.getWrapperClassNames(`${this.prefixCls}-intl`, ...args);
    }
    getSuffix() {
        const { suffix } = this.props;
        return this.wrapperSuffix(this.loading ? (React.createElement(Progress, { size: Size.small, type: ProgressType.loading })) : (suffix || React.createElement(Icon, { className: `${this.prefixCls}-intl`, type: "language" })), {
            onClick: debounce(this.openModal, 200),
        });
    }
    handleEnterDown(e) {
        if (this.props.type === IntlType.multipleLine) {
            super.handleEnterDown(e);
        }
        else {
            TextField.prototype.handleEnterDown.call(this, e);
        }
    }
    componentWillReceiveProps(nextProps, nextContext) {
        if (nextProps.type !== this.props.type) {
            const suffixCls = nextProps.type !== IntlType.multipleLine ? 'input' : 'textarea';
            this.prefixCls = this.getContextProPrefixCls(suffixCls, nextProps.prefixCls);
        }
        super.componentWillReceiveProps(nextProps, nextContext);
    }
    componentWillUnmount() {
        if (this.modal) {
            this.modal.close();
        }
    }
    renderWrapper() {
        if (this.props.displayOutput) {
            return this.renderOutput();
        }
        if (this.props.type === IntlType.multipleLine) {
            return super.renderWrapper();
        }
        return this.renderGroup();
    }
    showTooltip(e) {
        if (super.showTooltip(e)) {
            return true;
        }
        if (this.props.displayOutput) {
            const { getTooltip, getTooltipTheme, getTooltipPlacement } = this.context;
            const { tooltip = getTooltip('output') } = this.props;
            const { element, field } = this;
            if (element && !(field && field.get('multiLine', this.record)) && (tooltip === TextTooltip.always || (tooltip === TextTooltip.overflow && isOverflow(element)))) {
                const title = this.getRenderedValue();
                if (title) {
                    show(element, {
                        title,
                        placement: getTooltipPlacement('output') || 'right',
                        theme: getTooltipTheme('output'),
                    });
                    return true;
                }
            }
        }
        return false;
    }
    renderOutput() {
        const result = this.getRenderedValue();
        const text = isEmpty(result) || (isArrayLike(result) && !result.length) ? this.getContextConfig('renderEmpty')('Output') : result;
        const floatLabel = this.renderFloatLabel();
        const suffix = this.getSuffix();
        const className = classNames(`${this.prefixCls}-intl-wrapper`);
        if (floatLabel) {
            return (React.createElement("span", Object.assign({}, this.getWrapperProps()),
                floatLabel,
                React.createElement("span", { className: className },
                    React.createElement("span", Object.assign({}, this.getOtherProps()), text),
                    suffix)));
        }
        return (React.createElement("span", { className: className },
            React.createElement("span", Object.assign({}, this.getMergedProps()), text),
            suffix));
    }
};
IntlField.displayName = 'IntlField';
IntlField.defaultProps = {
    ...TextArea.defaultProps,
    rows: 3,
    resize: ResizeType.vertical,
    type: IntlType.singleLine,
};
__decorate([
    observable
], IntlField.prototype, "loading", void 0);
__decorate([
    computed
], IntlField.prototype, "readOnly", null);
__decorate([
    autobind,
    action
], IntlField.prototype, "saveSuffixRef", null);
__decorate([
    action
], IntlField.prototype, "setLoading", null);
__decorate([
    autobind
], IntlField.prototype, "handleIntlListOk", null);
__decorate([
    autobind
], IntlField.prototype, "handleIntlListCancel", null);
__decorate([
    autobind
], IntlField.prototype, "handleKeyDown", null);
__decorate([
    autobind
], IntlField.prototype, "handleBlur", null);
IntlField = __decorate([
    observer
], IntlField);
export default IntlField;
